/**
 * UTM & attribution tracking — Studio Cent Douze
 *
 * Captures marketing attribution data on the first visit and persists it in
 * localStorage. The stored payload is attached to every brief submission and
 * pushed to GA4 / GTM dataLayer for lead-source analytics.
 *
 * Fields captured:
 *   utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *   gclid   (Google Ads click ID)
 *   fbclid  (Meta / Facebook click ID)
 *   referrer, landing_page, first_seen_at
 *
 * Rules:
 *   1. Only overwrite the stored value if the *new* visit carries at least one
 *      UTM parameter or a click ID. This preserves the original source when the
 *      user comes back organically.
 *   2. Everything is client-side; no PII stored.
 */

const STORAGE_KEY = "scd_attribution_v1";

const ATTR_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "fbclid",
];

function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function safeReferrer() {
    try {
        const ref = document.referrer || "";
        if (!ref) return "";
        // Ignore internal referrers
        const host = new URL(ref).hostname;
        if (host === window.location.hostname) return "";
        return ref;
    } catch {
        return "";
    }
}

/**
 * Reads current URL search params, returns an object of tracked keys that are
 * present (non-empty).
 */
function readUrlAttribution() {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    for (const key of ATTR_KEYS) {
        const val = params.get(key);
        if (val && val.trim()) {
            found[key] = val.trim().slice(0, 200); // hard cap length
        }
    }
    return found;
}

/**
 * Should be called ONCE on app boot (e.g. in Home.jsx).
 * Captures the current URL's attribution if any, and stores it iff nothing
 * has ever been stored OR the new visit brings a real attribution signal.
 */
export function captureAttribution() {
    if (typeof window === "undefined") return null;

    const fromUrl = readUrlAttribution();
    const stored = readStored();

    const hasNewSignal = Object.keys(fromUrl).length > 0;

    if (!stored) {
        // First ever visit — store even if no UTM (baseline "direct" attribution)
        const payload = {
            ...fromUrl,
            referrer: safeReferrer(),
            landing_page: window.location.href.slice(0, 500),
            first_seen_at: new Date().toISOString(),
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch {
            /* quota / private mode — ignore */
        }
        return payload;
    }

    if (hasNewSignal) {
        // A campaign click overrides the previous attribution (last-click model)
        const payload = {
            ...stored,
            ...fromUrl,
            referrer: safeReferrer() || stored.referrer || "",
            landing_page: window.location.href.slice(0, 500),
            last_touch_at: new Date().toISOString(),
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch {
            /* ignore */
        }
        return payload;
    }

    return stored;
}

/**
 * Returns the attribution payload to attach to a lead submission.
 * Safe to call anywhere, anytime.
 */
export function getAttribution() {
    if (typeof window === "undefined") return {};
    const stored = readStored() || {};
    return {
        utm_source: stored.utm_source || "",
        utm_medium: stored.utm_medium || "",
        utm_campaign: stored.utm_campaign || "",
        utm_term: stored.utm_term || "",
        utm_content: stored.utm_content || "",
        gclid: stored.gclid || "",
        fbclid: stored.fbclid || "",
        referrer: stored.referrer || "",
        landing_page: stored.landing_page || "",
        first_seen_at: stored.first_seen_at || "",
    };
}

/**
 * Pushes a GA4 / GTM "generate_lead" event to window.dataLayer.
 * No-op if dataLayer isn't ready.
 */
export function pushLeadEvent({ reference, value, currency = "CAD" } = {}) {
    if (typeof window === "undefined") return;
    if (!Array.isArray(window.dataLayer)) return;
    const attribution = getAttribution();
    try {
        window.dataLayer.push({
            event: "generate_lead",
            lead_reference: reference || "",
            value: Number(value) || 0,
            currency,
            ...attribution,
        });
    } catch {
        /* ignore */
    }
}

/**
 * For debugging in the browser console:
 *   window.__scdAttribution()
 */
if (typeof window !== "undefined") {
    window.__scdAttribution = getAttribution;
}
